import React, { useState, useEffect, useCallback } from 'react';
import { ElectionService, MemberService, VoteService } from '../services/firebaseService';
import { Election, Member, Vote } from '../types';
import CandidatePhoto from '../components/CandidatePhoto';
import { 
  ClockIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface ElectionLiveData {
  election: Election;
  totalVotes: number;
  uniqueVoters: number;
  abstentions: number;
  validVotes: number;
  participationRate: number;
  timeRemaining: string;
  isActive: boolean;
  candidateResults: Array<{
    candidate: any;
    votes: number;
    percentage: number;
  }>;
}

const AdminLiveTracking: React.FC = () => {
  const [elections, setElections] = useState<Election[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  // const [voteGroups] = useState<VoteGroup[]>([]); // Pas utilisé pour l'instant
  const [liveData, setLiveData] = useState<ElectionLiveData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true); // Afficher uniquement les élections en live

  // Calculer le temps restant
  const getTimeRemaining = (endDate: Date): { text: string; isActive: boolean } => {
    const now = new Date();
    const timeLeft = endDate.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      return { text: 'Terminé', isActive: false };
    }
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return { text: `${days}j ${hours}h`, isActive: true };
    } else if (hours > 0) {
      return { text: `${hours}h ${minutes}m`, isActive: true };
    } else {
      return { text: `${minutes}m`, isActive: true };
    }
  };

  // Charger les données
  const loadData = async () => {
    try {
        const [electionsData, membersData, votesData] = await Promise.all([
          ElectionService.getAllElections(),
          MemberService.getAllMembers(),
          VoteService.getAllVotes()
          // VoteGroupService.getAllVoteGroups() // Pas utilisé pour l'instant
        ]);
      
      setElections(electionsData);
      setMembers(membersData);
      setVotes(votesData);
        // setVoteGroups(groupsData); // Pas utilisé pour l'instant
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Calculer les données en temps réel
  const calculateLiveData = useCallback(() => {
    const activeMembers = members.filter(m => m.membershipStatus === 'active' && m.hasVotingRights);
    
    const liveData: ElectionLiveData[] = elections.map(election => {
      const electionVotes = votes.filter(v => v.electionId === election.id);
      const uniqueVoters = new Set(electionVotes.map(v => v.memberId)).size;
      const abstentions = electionVotes.filter(v => v.isAbstention).length;
      const validVotes = electionVotes.length - abstentions;
      const participationRate = activeMembers.length > 0 ? (uniqueVoters / activeMembers.length) * 100 : 0;
      
      const timeInfo = getTimeRemaining(election.endDate);
      
      // Calculer les résultats par candidat
      const candidateResults = election.candidates.map(candidate => {
        const candidateVotes = electionVotes.filter(v => v.candidateId === candidate.id).length;
        const percentage = electionVotes.length > 0 ? (candidateVotes / electionVotes.length) * 100 : 0;
        
        return {
          candidate,
          votes: candidateVotes,
          percentage
        };
      }).sort((a, b) => b.votes - a.votes);
      
      return {
        election,
        totalVotes: electionVotes.length,
        uniqueVoters,
        abstentions,
        validVotes,
        participationRate,
        timeRemaining: timeInfo.text,
        isActive: timeInfo.isActive && election.isActive,
        candidateResults
      };
    }).sort((a, b) => {
      // Trier par: actives d'abord, puis par nombre de votes
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;
      return b.totalVotes - a.totalVotes;
    });
    
    setLiveData(liveData);
  }, [elections, members, votes]);

  useEffect(() => {
    loadData();
    
    // Actualisation automatique toutes les 10 secondes
    const interval = setInterval(loadData, 10000);
    
    // Raccourcis clavier
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'r' || e.key === 'R') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          loadData();
        }
      } else if (e.key === 'c' || e.key === 'C') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          setCompactMode(prev => !prev);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    if (elections.length > 0 && members.length > 0 && votes.length >= 0) {
      calculateLiveData();
    }
  }, [elections, members, votes, calculateLiveData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600">Chargement du suivi en temps réel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-lg p-8 max-w-md mx-4 shadow-lg">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="btn-primary"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const activeElections = liveData.filter(e => e.isActive);
  const totalActiveVotes = activeElections.reduce((sum, e) => sum + e.totalVotes, 0);
  const totalUniqueVoters = new Set(activeElections.flatMap(e => 
    votes.filter(v => v.electionId === e.election.id).map(v => v.memberId)
  )).size;
  const activeMembers = members.filter(m => m.membershipStatus === 'active' && m.hasVotingRights);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête fixe */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="w-8 h-8 mr-3 text-blue-600" />
                Suivi en Temps Réel
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Dernière mise à jour: {lastUpdate.toLocaleTimeString('fr-FR')} • 
                <span className="text-green-600 font-medium"> Mise à jour automatique toutes les 10s</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Raccourcis: F11 (plein écran) • Ctrl+R (actualiser) • Ctrl+C (mode compact)
              </p>
            </div>
            <div className="flex items-center space-x-6">
              {/* Statistiques globales */}
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{activeElections.length}</p>
                <p className="text-xs text-gray-500">Élections actives</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{totalActiveVotes}</p>
                <p className="text-xs text-gray-500">Votes totaux</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{totalUniqueVoters}</p>
                <p className="text-xs text-gray-500">Votants uniques</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {activeMembers.length > 0 ? ((totalUniqueVoters / activeMembers.length) * 100).toFixed(1) : 0}%
                </p>
                <p className="text-xs text-gray-500">Participation</p>
              </div>
              <button
                onClick={loadData}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                title="Actualiser (Ctrl+R)"
              >
                <ArrowPathIcon className="w-4 h-4 mr-2" />
                Actualiser
              </button>
              <button
                onClick={() => setShowOnlyActive(!showOnlyActive)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                  showOnlyActive 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title="Afficher uniquement les élections en direct"
              >
                <ChartBarIcon className="w-4 h-4 mr-2" />
                {showOnlyActive ? 'En direct' : 'Toutes'}
              </button>
              <button
                onClick={toggleFullscreen}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
                title="Plein écran (F11)"
              >
                <EyeIcon className="w-4 h-4 mr-2" />
                {isFullscreen ? 'Sortir' : 'Plein écran'}
              </button>
              <button
                onClick={() => setCompactMode(!compactMode)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors duration-200 ${
                  compactMode 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title="Mode compact (Ctrl+C)"
              >
                <ChartBarIcon className="w-4 h-4 mr-2" />
                {compactMode ? 'Normal' : 'Compact'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal - optimisé pour grand écran */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {liveData.length === 0 ? (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune élection</h3>
            <p className="text-gray-500">Aucune élection n'est configurée dans le système.</p>
          </div>
        ) : (
          <div className={`grid gap-6 ${
            compactMode ? (
              liveData.length <= 4 ? 'grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6' :
              liveData.length <= 8 ? 'grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6 3xl:grid-cols-8' :
              'grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6 3xl:grid-cols-8 4xl:grid-cols-10'
            ) : (
              liveData.length <= 2 ? 'grid-cols-1 xl:grid-cols-2' :
              liveData.length <= 4 ? 'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-2' :
              liveData.length <= 6 ? 'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3' :
              'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4'
            )
          }`}>
            {(showOnlyActive ? liveData.filter(d => d.isActive) : liveData).map((data) => (
              <div
                key={data.election.id}
                className={`bg-white rounded-xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                  data.isActive 
                    ? 'border-green-200 hover:border-green-300' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* En-tête de l'élection */}
                <div className={`${compactMode ? 'px-4 py-3' : 'px-6 py-4'} rounded-t-xl ${
                  data.isActive ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-gray-400 to-gray-500'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      <h3 className={`${compactMode ? 'text-sm' : 'text-lg'} font-bold truncate`}>{data.election.title}</h3>
                      {!compactMode && (
                        <p className="text-sm text-green-100 truncate">{data.election.position}</p>
                      )}
                    </div>
                    <div className="text-right text-white">
                      <div className="flex items-center">
                        <ClockIcon className={`${compactMode ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                        <span className={`${compactMode ? 'text-xs' : 'text-sm'} font-medium`}>{data.timeRemaining}</span>
                      </div>
                      {!compactMode && (
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            data.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {data.isActive ? 'Active' : 'Terminée'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Statistiques principales */}
                <div className={`${compactMode ? 'px-4 py-3' : 'px-6 py-4'}`}>
                  <div className={`grid ${compactMode ? 'grid-cols-2 gap-2 mb-3' : 'grid-cols-2 gap-4 mb-4'}`}>
                    <div className="text-center">
                      <p className={`${compactMode ? 'text-lg' : 'text-2xl'} font-bold text-gray-900`}>{data.totalVotes}</p>
                      <p className="text-xs text-gray-500">Votes</p>
                    </div>
                    <div className="text-center">
                      <p className={`${compactMode ? 'text-lg' : 'text-2xl'} font-bold text-blue-600`}>{data.uniqueVoters}</p>
                      <p className="text-xs text-gray-500">Votants</p>
                    </div>
                    {!compactMode && (
                      <>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{data.validVotes}</p>
                          <p className="text-xs text-gray-500">Votes valides</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-orange-600">{data.abstentions}</p>
                          <p className="text-xs text-gray-500">Abstentions</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Barre de participation */}
                  <div className={`${compactMode ? 'mb-3' : 'mb-4'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`${compactMode ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>Participation</span>
                      <span className={`${compactMode ? 'text-xs' : 'text-sm'} font-bold text-gray-900`}>{data.participationRate.toFixed(1)}%</span>
                    </div>
                    <div className={`w-full bg-gray-200 rounded-full ${compactMode ? 'h-2' : 'h-3'}`}>
                      <div
                        className={`${compactMode ? 'h-2' : 'h-3'} rounded-full transition-all duration-500 ${
                          data.participationRate >= 80 ? 'bg-green-500' :
                          data.participationRate >= 60 ? 'bg-yellow-500' :
                          data.participationRate >= 40 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(data.participationRate, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Résultats des candidats */}
                  {!compactMode && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Résultats</h4>
                      {data.candidateResults.length > 0 ? (
                        data.candidateResults.slice(0, 3).map((result, index) => (
                          <div key={result.candidate.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <span className="text-sm font-bold text-gray-600 mr-2">#{index + 1}</span>
                              <CandidatePhoto 
                                photo={result.candidate.photo} 
                                name={result.candidate.name} 
                                size="sm" 
                                className="mr-2"
                              />
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {result.candidate.name}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${result.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-bold text-gray-900 w-12 text-right">
                                {result.votes}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">Aucun vote enregistré</p>
                      )}
                    </div>
                  )}

                  {/* Mode compact - affichage simplifié du gagnant */}
                  {compactMode && data.candidateResults.length > 0 && (
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-2">Gagnant</p>
                      <CandidatePhoto 
                        photo={data.candidateResults[0].candidate.photo} 
                        name={data.candidateResults[0].candidate.name} 
                        size="md" 
                        className="mx-auto mb-2"
                      />
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {data.candidateResults[0].candidate.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {data.candidateResults[0].votes} votes ({data.candidateResults[0].percentage.toFixed(1)}%)
                      </p>
                    </div>
                  )}

                  {/* Informations supplémentaires */}
                  {!compactMode && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{data.election.candidates.length} candidat(s)</span>
                        <span>
                          {data.election.startDate.toLocaleDateString()} - {data.election.endDate.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLiveTracking;
